import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { finance } from '../../services/api';
import StatCard from '../../components/common/StatCard';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import { theme } from '../../theme';

function FinanceDashboard() {
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    finance.getFinanceDashboard().then((r) => {
      setDashData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  const stats = dashData || {};
  const monthlyData = stats.monthly_data || [];
  const recentClaims = stats.recent_claims || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Finance Dashboard</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Claims and financial overview</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <StatCard icon="📤" label="Submitted" value={stats.count_submitted ?? 0} color={theme.primary} subtext={`₹${Number(stats.amount_submitted ?? 0).toLocaleString('en-IN')}`} />
        <StatCard icon="🔍" label="Waiting for Verification" value={stats.count_pending ?? 0} color={theme.warning} subtext={`₹${Number(stats.amount_pending ?? 0).toLocaleString('en-IN')}`} />
        <StatCard icon="✅" label="Approved" value={stats.count_approved ?? 0} color={theme.success} subtext={`₹${Number(stats.amount_approved ?? 0).toLocaleString('en-IN')}`} />
        <StatCard icon="💰" label="Paid" value={stats.count_paid ?? 0} color="#10B981" subtext={`₹${Number(stats.amount_paid ?? 0).toLocaleString('en-IN')}`} />
        <StatCard icon="⏳" label="Pending Payment" value={stats.pending_payment_count ?? 0} color="#F97316" subtext={`₹${Number(stats.pending_payment_amount ?? 0).toLocaleString('en-IN')}`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <Card title="Monthly Claims Amount">
          {monthlyData.length === 0 ? (
            <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: theme.textMuted }}>No monthly data available</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: theme.textMuted }} />
                <YAxis tick={{ fontSize: 11, fill: theme.textMuted }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.5rem' }}
                  formatter={(v) => [`₹${Number(v).toLocaleString()}`, 'Amount']}
                />
                <Bar dataKey="amount" fill={theme.primary} radius={[4, 4, 0, 0]} name="Amount" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Recent Claims">
          {recentClaims.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '2rem 0' }}>No recent claims</p>
          ) : recentClaims.map((c, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: i < recentClaims.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>{c.title}</p>
                <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>
                  {c.employee_name || c.employee?.full_name} · ₹{Number(c.total_amount || c.amount || 0).toLocaleString()}
                </p>
              </div>
              <Badge status={c.status} />
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

export default FinanceDashboard;
